/* import __COLOCATED_TEMPLATE__ from './vat-field.hbs'; */
/* RESPONSIBLE TEAM: team-billing */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import nonEuTaxEligibleCountries from 'embercom/lib/non-eu-tax-eligible-countries';
import { validateVatNumber, getVatValidationErrorKey } from 'embercom/utils/vat-validation';

export interface Args {
  setVatNumber: (vatNumber: Event) => void;
  countryCode?: string;
  vatNumber?: string | null;
}

interface Signature {
  Args: Args;
}

export default class VatField extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked validationErrorKey: string | null = null;

  /**
   * Returns the appropriate placeholder translation key based on the country code
   */
  get placeholderKey(): string {
    let { countryCode } = this.args;
    let baseKey = 'signup.teams.pricing5.annual-plans.credit-card-details';

    let placeholderCountry = 'default';
    if (countryCode && nonEuTaxEligibleCountries.includes(countryCode)) {
      placeholderCountry = countryCode.toLowerCase();
    }

    return `${baseKey}.${placeholderCountry}-tax-number-placeholder`;
  }

  /**
   * Returns the translated placeholder text
   */
  get placeholder(): string {
    return this.intl.t(this.placeholderKey, { countryCode: this.args.countryCode });
  }

  /**
   * Gets the current VAT number from the arguments
   */
  get vatNumber() {
    return this.args.vatNumber;
  }

  /**
   * Gets the translated validation error message
   */
  get validationError(): string | null {
    return this.validationErrorKey ? this.intl.t(this.validationErrorKey) : null;
  }

  /**
   * Validates the VAT number and calls the parent's setVatNumber
   */
  @action
  onVatNumberInput(event: Event) {
    let target = event.target as HTMLInputElement;
    let vatNumber = target.value;

    // Clear previous validation error
    this.validationErrorKey = null;

    // Validate if there's a value
    if (
      vatNumber &&
      this.args.countryCode &&
      !validateVatNumber(vatNumber, this.args.countryCode)
    ) {
      this.validationErrorKey = getVatValidationErrorKey(this.args.countryCode);
    }

    // Call the parent's handler
    this.args.setVatNumber(event);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Stripe::VatField': typeof VatField;
  }
}
